import React, { useState, useEffect } from 'react';
import Delete from '@mui/icons-material/Delete';
import LinkedIn from '@mui/icons-material/LinkedIn';
import RemoveRedEye from '@mui/icons-material/RemoveRedEye';
import { IconButton, Tooltip, Typography, Box, Chip } from '@mui/material';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { displaySwipeableDrawerSummaryContact } from '../../../redux/SwipeableDrawer/actions/swipeableDrawerActions';
import { getUserData, getRowData } from '../../../utils/FunctionHelpers';
import DialogDeleteCustom from '../../../components/DialogDeleteCustom/DialogDeleteCustom';
import { deleteContact } from '../../../services/ContactService';
import { withSnackbar } from '../../../utils/SnackbarHOC';
import { handleOnViewColumnsChange, displayColumn } from '../../../utils/viewColumns';
import { handleChangeRowsPerPage, getRowsPerPage } from '../../../utils/DataTables.js';
import { debounce } from 'lodash';
import moment from 'moment';
import { commonGridSx, gridContainerSx, gridLocaleText, makeGridToolbar } from '../../../utils/gridStyles';
import { DataGrid } from '../../../components/SortableDataGrid/SortableDataGrid';

const tableName = 'contactColumnVisibility';
const CustomToolbar = makeGridToolbar('Contacts');

const ContactList = ({ contactsData, handleSearchContacts, snackbarShowMessage, parameters }) => {
  const dispatch = useDispatch();
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [contactSelectedToDelete, setContactSelectedToDelete] = useState(null);
  const [loadToDelete, setLoadToDelete] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    page: (contactsData.pageContact || 1) - 1,
    pageSize: getRowsPerPage(tableName) ?? contactsData.limit ?? 50,
  });
  const [sortModel, setSortModel] = useState(
    contactsData.sorting?.name
      ? [{ field: contactsData.sorting.name, sort: contactsData.sorting.order }]
      : []
  );

  const handleCloseDialog = () => { if (!loadToDelete) setOpenDialogDelete(false); };
  const handleOpenDialog = (contact) => {
    setContactSelectedToDelete({ id: contact.id, fullName: contact.fullName.charAt(0).toUpperCase() + contact.fullName.slice(1) });
    setOpenDialogDelete(true);
  };

  const handleDeleteContact = async (uuid) => {
    try {
      setLoadToDelete(true);
      snackbarShowMessage('Patientez svp...', 'warning', null);
      const data = await deleteContact(uuid);
      handleSearchContacts(contactsData.pageContact, parameters);
      snackbarShowMessage(data);
    } catch {
      snackbarShowMessage("Erreur à la suppression du contact", 'error', 4000);
    } finally {
      setLoadToDelete(false);
      setOpenDialogDelete(false);
    }
  };

  const debouncedSearch = debounce((searchText) => {
    handleSearchContacts(1, {
      ...parameters,
      column: sortModel[0]?.field || '',
      sort: sortModel[0]?.sort || 'asc',
      search: searchText,
    }, paginationModel.pageSize);
  }, 800);

  const handlePaginationModelChange = (newModel) => {
    if (newModel.pageSize !== paginationModel.pageSize) {
      handleChangeRowsPerPage(tableName, newModel.pageSize);
      handleSearchContacts(1, parameters, newModel.pageSize);
    } else {
      handleSearchContacts(newModel.page + 1, {
        ...parameters,
        column: sortModel[0]?.field || '',
        sort: sortModel[0]?.sort || 'asc',
      }, newModel.pageSize);
    }
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    handleSearchContacts(paginationModel.page + 1, {
      ...parameters,
      column: newSortModel[0]?.field || '',
      sort: newSortModel[0]?.sort || 'asc',
    }, paginationModel.pageSize);
  };

  const handleFilterModelChange = (filterModel) => {
    const searchText = filterModel.quickFilterValues?.join(' ') || '';
    debouncedSearch(searchText);
  };

  const rows = (contactsData.contacts || []).map((c) => ({
    ...c,
    _id: c.uuid,
    updatedAt: c.updatedAt?.date ? c.updatedAt.date : c.updatedAt,
  }));

  const columns = [
    {
      field: 'updatedAt', headerName: 'DATE MAJ', width: 110,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>
          {p.value ? moment(p.value).format('DD/MM/YYYY') : '—'}
        </Typography>
      ),
    },
    {
      field: 'prenom', headerName: 'PRÉNOM', width: 150,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
          <Tooltip title="Voir résumé">
            <IconButton size="small"
              onClick={() => dispatch(displaySwipeableDrawerSummaryContact({ show: true, idContact: p.row.uuid, idSociete: p.row.societeId }))}
              sx={{ color: '#94a3b8', p: 0.25, '&:hover': { color: '#03407b', background: '#ebf4fb' }, borderRadius: 1, flexShrink: 0 }}>
              <RemoveRedEye sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Link to={{ pathname: `/societe/${p.row.societeId}/contacts/${p.row.uuid}/view` }}
            style={{ textDecoration: 'none', color: '#334155', fontSize: '1rem', textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {p.value}
          </Link>
        </Box>
      ),
    },
    {
      field: 'nom', headerName: 'NOM', width: 140,
      renderCell: (p) => (
        <Link to={{ pathname: `/societe/${p.row.societeId}/contacts/${p.row.uuid}/view` }}
          style={{ textDecoration: 'none', fontWeight: 600, color: '#0f172a', fontSize: '1rem', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value}
        </Link>
      ),
    },
    {
      field: 'step', headerName: 'STEP', width: 130,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'statut', headerName: 'STATUT', width: 120,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'fonction', headerName: 'FONCTION', width: 170,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569', textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'societe', headerName: 'SOCIÉTÉ', width: 150,
      renderCell: (p) => (
        <Link to={{ pathname: `/societe/${p.row.societeId}/view` }}
          style={{ textDecoration: 'none', color: '#0ea5e9', fontWeight: 500, fontSize: '1rem', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value ?? '—'}
        </Link>
      ),
    },
    {
      field: 'missionsCount', headerName: 'MISSIONS', width: 100, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <Link to={{ pathname: `/societe/${p.row.societeId}/contacts/${p.row.uuid}/view`, state: { tabIndex: 3 } }}
          style={{ textDecoration: 'none' }}>
          <Chip label={p.value ?? 0} size="small" sx={{ background: '#ebf4fb', color: '#03407b', fontWeight: 600, fontSize: '0.8125rem', cursor: 'pointer' }} />
        </Link>
      ),
    },
    {
      field: 'mobile', headerName: 'MOBILE', width: 130,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#334155' }}>{p.value || '—'}</Typography>,
    },
    {
      field: 'telFix', headerName: 'TÉL PRO', width: 130,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#334155' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'email', headerName: 'EMAIL PRO', width: 210,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'linkedIn', headerName: 'LKD', width: 60, align: 'center', headerAlign: 'center', sortable: false,
      renderCell: (p) => p.value ? (
        <Tooltip title="Voir LinkedIn">
          <a href={p.value} target="_blank" rel="noreferrer" style={{ color: '#0a66c2', lineHeight: 0 }}>
            <LinkedIn sx={{ fontSize: 20 }} />
          </a>
        </Tooltip>
      ) : <Typography sx={{ color: '#cbd5e1', fontSize: '1rem' }}>—</Typography>,
    },
    {
      field: 'localisation', headerName: 'LOCALISATION', width: 140,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'deliveryManager', headerName: 'DELIVERY MANAGER', width: 170,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'actions', headerName: 'ACTIONS', width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {getUserData()?.role?.includes('ROLE_DIRECTION') && (
            <Tooltip title="Supprimer">
              <IconButton size="small"
                onClick={() => handleOpenDialog({ id: p.row.uuid, fullName: `${p.row.prenom} ${p.row.nom}` })}
                sx={{ color: '#ef4444', background: '#fef2f2', borderRadius: '8px', p: 0.75, '&:hover': { background: '#fee2e2' } }}>
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <Box sx={gridContainerSx}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.uuid}
          loading={contactsData.isLoading}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          rowCount={contactsData.contactsCount ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[50, 100]}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onFilterModelChange={handleFilterModelChange}
          disableRowSelectionOnClick
          showToolbar
          slots={{ toolbar: CustomToolbar }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          localeText={gridLocaleText}
          sx={commonGridSx}
        />
      </Box>
      <DialogDeleteCustom
        handleClose={handleCloseDialog}
        open={openDialogDelete}
        loading={loadToDelete}
        title="Supprimer le contact"
        handleConfirm={() => handleDeleteContact(contactSelectedToDelete?.id)}
        label={`Voulez-vous vraiment supprimer le contact ${contactSelectedToDelete?.fullName} ?`}
      />
    </>
  );
};

export default withSnackbar(ContactList);


